import { MantineProvider, MantineThemeOverride } from '@mantine/core';
import { useMediaQuery, useOs } from '@mantine/hooks';
import { Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { useCompany, useConfig } from './api/config';
import { useOccasion } from './api/occasion';
import Loader from './components/Loader';
import { updateCompany, updateConfig } from './services/config';
import { loadOccasions } from './services/occasion';
import { CustomFonts } from './styles/font';
import { theme } from './theme/theme';
import storage from './utils/storage';
import { getColorTheme } from './utils/theme';
import { AppRoutes } from '@/routes';

const App = () => {
  const currentLocation = storage.getItem('currentLocation');
  const os = useOs();
  const isMobile = useMediaQuery('(max-width: 768px)', true);
  const [loadTheme, setLoadTheme] = useState<MantineThemeOverride>();

  const disabledURL = ['/track-order', '/guest-order-v2/guest-approval-request'];

  const url = window.location.pathname;
  const isUrlDisabled = disabledURL.some((disabledUrl) => url.includes(disabledUrl));

  if (os !== undefined || os !== 'undertimed') {
    storage.setItem('userOs', os);
  }
  const { data: configData, isFetching: isConfigFetching } = useConfig({
    config: {
      enabled: !isUrlDisabled,
    },
  });

  const { data: companyData, isFetching: isCompanyFetching } = useCompany({
    config: {
      enabled: !!configData?.company_id,
    },
    companyId: configData?.company_id,
  });

  const { data: occasionData } = useOccasion({
    config: {
      enabled: !!(
        !!(currentLocation?.id ?? companyData?.locations?.data?.[0]?.id) &&
        Boolean(configData?.auth_skip)
      ),
      staleTime: 0,
    },
    params: {
      location_id: currentLocation?.id ?? companyData?.locations?.data?.[0]?.id,
    },
  });

  const emptyCache = () => {
    const clearCacheCount = sessionStorage.getItem('clear_cache_count');
    const count = clearCacheCount ? Number(clearCacheCount) + 1 : 1;
    sessionStorage.setItem('clear_cache_count', String(count));
    if (count < 5) {
      if ('caches' in window) {
        void caches.keys().then((names) => {
          names.forEach((name) => {
            void caches.delete(name);
          });
        });
      }
      if ('serviceWorker' in navigator) {
        void navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            void registration.unregister();
          });
        });
      }
      window.location.reload();
    }
  };

  useEffect(() => {
    if (configData && !isConfigFetching) {
      updateConfig(configData);
      getConfigData();
    } else {
      setLoadTheme(theme);
    }
  }, [configData, isConfigFetching]);

  useEffect(() => {
    if (companyData) {
      // void refetch();
      updateCompany(companyData);
    }
  }, [companyData]);

  useEffect(() => {
    if (occasionData) {
      loadOccasions(occasionData);
    }
  }, [occasionData]);

  useEffect(() => {
    if (configData?.clear_new_pwa_cache && !isConfigFetching) {
      emptyCache();
    }
  }, [configData, isConfigFetching]);

  if ('serviceWorker' in navigator) {
    // void navigator.serviceWorker.register('/sw.js');
    registerSW({
      onNeedRefresh() {},
      onOfflineReady() {},
    });
  }

  const getConfigData = () => {
    const config = storage.getItem('config');
    if (config) {
      try {
        setLoadTheme(() => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: getColorTheme(config),
          },
        }));
      } catch (error) {
        // console.log('error', error);
      }

      return true;
    }
    return false;
  };

  if (isConfigFetching || isCompanyFetching || !loadTheme) {
    return <Loader />;
  }

  return (
    <MantineProvider theme={loadTheme} withGlobalStyles withNormalizeCSS withCSSVariables>
      <CustomFonts />
      <Notifications
        position={isMobile ? 'bottom-center' : 'top-right'}
        limit={1}
        style={{
          zIndex: 9999,
        }}
        autoClose={2000}
      />
      <AppRoutes configData={configData} />
    </MantineProvider>
  );
};

export default Sentry.withProfiler(App);
